import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { HelpCircle } from "lucide-react";
import Modal from "./Modal";
import LoginForm from "./LoginForm";
import SignupForm from "./SignupForm";
import { LanguageSelector } from "./LanguageSelector";
import { RecordsBoard } from "./RecordsBoard";
import TacticDemo from "./TacticDemo";
import { useAuth } from "../auth/AuthContext";
import { startGuest } from "../api/guest";
import { serializeStatements } from "./tactic/tacticModel";
import { BASIC_STATEMENTS } from "./tactic/basicTactic";
import "./StartPage.css";

function FeatureCard({ icon, title, desc }) {
  const [flipped, setFlipped] = useState(false);
  const face = (
    <>
      <span className="start-feature-icon">{icon}</span>
      <h3 className="start-feature-title">{title}</h3>
      <p className="start-feature-desc">{desc}</p>
    </>
  );
  return (
    <div className="flip-card" onClick={() => setFlipped((f) => !f)}>
      <div className={`flip-card-inner ${flipped ? "flip-card-inner--flipped" : ""}`}>
        <div className="flip-card-face flip-card-front">{face}</div>
        <div className="flip-card-face flip-card-back">{face}</div>
      </div>
    </div>
  );
}

export default function StartPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [showLogin, setShowLogin]   = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [tryLoading, setTryLoading] = useState(false);
  const [tryError, setTryError]     = useState("");

  // "Try it": spin up an anonymous guest account (ready team + default tactic)
  // and drop the visitor straight into the team editor — no signup.
  const handleTry = async () => {
    setTryError("");
    setTryLoading(true);
    try {
      const { access_token } = await startGuest(serializeStatements(BASIC_STATEMENTS));
      await login(access_token);
      navigate("/team/edit");
    } catch (err) {
      setTryError(err?.status === 429 ? t("start.tryLimit") : t("start.tryError"));
      setTryLoading(false);
    }
  };

  return (
    <div className="start-page">
      <div className="start-lang">
        <LanguageSelector />
      </div>

      <div className="start-hero" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/match_b2_2.webp)`, backgroundSize: "auto", backgroundPosition: "center", backgroundRepeat: "no-repeat" }}>
        <h1 className="start-brand">TACTICS</h1>
        <p className="start-tagline">{t("start.tagline")}</p>
        <Link to="/help" className="start-help-link">
          <HelpCircle size={16} className="start-help-icon" />
          {t("start.howItWorks")}
        </Link>
        <button className="start-btn start-btn-primary start-try-btn" onClick={handleTry} disabled={tryLoading}>
          {tryLoading ? t("start.tryLoading") : t("start.tryIt")}
        </button>
        <span className="start-try-sub">{t("start.trySub")}</span>
        {tryError && <span className="start-try-error">{tryError}</span>}
        <div className="start-actions">
          <button className="start-btn start-btn-secondary" onClick={() => setShowLogin(true)}>
            {t("start.login")}
          </button>
          <button className="start-btn start-btn-secondary" onClick={() => setShowSignUp(true)}>
            {t("start.signup")}
          </button>
        </div>
        <RecordsBoard />
      </div>

      <div className="start-below">
        <div className="start-section">
          <h2 className="start-section-title">{t("start.previewTitle")}</h2>
          <p className="start-section-sub">{t("start.previewSub")}</p>
          <TacticDemo />
        </div>

        <div className="start-steps">
          <div className="start-step">
            <span className="start-step-num">1</span>
            <span className="start-step-label">{t("start.step1")}</span>
          </div>
          <div className="start-step-arrow">→</div>
          <div className="start-step">
            <span className="start-step-num">2</span>
            <span className="start-step-label">{t("start.step2")}</span>
          </div>
          <div className="start-step-arrow">→</div>
          <div className="start-step">
            <span className="start-step-num">3</span>
            <span className="start-step-label">{t("start.step3")}</span>
          </div>
          <div className="start-step-arrow">→</div>
          <div className="start-step">
            <span className="start-step-num">4</span>
            <span className="start-step-label">{t("start.step4")}</span>
          </div>
        </div>

        <div className="start-features">
          <h2 className="start-section-title">{t("start.featTitle")}</h2>
          <div className="start-feature-cards">
            <FeatureCard icon="🧠" title={t("start.feat1Title")} desc={t("start.feat1")} />
            <FeatureCard icon="⚽" title={t("start.feat2Title")} desc={t("start.feat2")} />
            <FeatureCard icon="🏆" title={t("start.feat3Title")} desc={t("start.feat3")} />
            <FeatureCard icon="🤝" title={t("start.feat4Title")} desc={t("start.feat4")} />
          </div>
        </div>

        <button className="start-btn start-btn-primary start-cta" onClick={() => setShowSignUp(true)}>
          {t("start.cta")}
        </button>

        <a href="/teachers/index.html" className="start-teachers-banner">🎓 {t("start.teachersBanner")} →</a>
      </div>

      <span className="start-footer">© {new Date().getFullYear()} Tactics</span>

      {showLogin && (
        <Modal onClose={() => setShowLogin(false)}>
          <LoginForm onSuccess={() => setShowLogin(false)} onCancel={() => setShowLogin(false)} />
        </Modal>
      )}

      {showSignUp && (
        <Modal onClose={() => setShowSignUp(false)}>
          <SignupForm onClose={() => setShowSignUp(false)} />
        </Modal>
      )}
    </div>
  );
}
